import React, { useEffect, useRef, useState } from "react";

import { Editor, Monaco } from "@monaco-editor/react";
import { DashboardComponentType, IDashboardComponent } from "api/model/dashboard/DashboardComponent";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { T } from "services/lang/LanguageService";
import { schema } from "../schemas/componentSchema";

type ComponentConfigurationProps = {
  component: IDashboardComponent;
  onChange: (newComponent: IDashboardComponent) => void;
  closeDialog: () => void;
};

const ComponentConfiguration: React.FC<ComponentConfigurationProps> = props => {
  const { title: titleGiven, type: typeGiven, ...config } = props.component;

  const types: { label: string; value: DashboardComponentType }[] = [
    { label: T("label.product-template-table"), value: "ProductTemplateTable" },
    { label: T("label.dataset-line-chart"), value: "DatasetLineChart" },
    { label: T("label.dataset-scatter-chart"), value: "DatasetScatterChart" },
    { label: T("label.dataset-sankey-chart"), value: "DatasetSankeyChart" },
    { label: T("label.dataset-table"), value: "DatasetTable" },
    { label: T("label.dataset-pie-chart"), value: "DatasetPieChart" },
    { label: T("label.dataset-histogram-chart"), value: "DatasetHistogramChart" },
    { label: T("label.dataset-bar-chart"), value: "DatasetBarChart" },
    { label: T("label.free-text"), value: "FreeText" },
    { label: T("label.failing-component"), value: "FailingComponentTest" },
    { label: T("label.value-change-indicator"), value: "ValueChangeIndicator" },
    { label: T("word.separator"), value: "Separator" },
    { label: T("label.periodic-data-chart"), value: "PeriodicDataChart" },
    { label: T("label.current-date-display"), value: "CurrentDateDisplay" },
    { label: T("label.line-chart-legend"), value: "LineChartLegend" },
  ];

  const monacoRef = useRef<Monaco | null>(null);

  const [title, setTitle] = useState(titleGiven || "");
  const [type, setType] = useState<any>(typeGiven);
  const [value, setValue] = useState<string | undefined>(JSON.stringify(config, null, 2));
  const [error, setError] = useState(false);

  useEffect(() => {
    return () => {
      monacoRef.current?.languages.json.jsonDefaults.setDiagnosticsOptions({});
    };
  }, []);

  const onEditorChange = (newValue: string | undefined) => {
    setValue(newValue);
  };

  const onEditorMounted = (editor: any, monaco: Monaco) => {
    monacoRef.current = monaco;
    monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
      validate: true,
      schemas: [
        {
          uri: "https://json-schema.org/",
          fileMatch: ["*"],
          schema,
        },
      ],
    });
  };

  const onSaveButtonClick = () => {
    const val = JSON.parse(value!!);
    props.onChange({
      title,
      type,
      ...val,
    });
    props.closeDialog();
  };

  return (
    <div className="flex flex-column gap-2">
      <div className="flex gap-2">
        <div className="flex flex-column gap-1">
          <b>{T("word.title")}</b>
          <InputText value={title} onChange={e => setTitle(e.target.value)} />
        </div>

        <div className="flex flex-column gap-1">
          <b>{T("word.type")}</b>
          <Dropdown
            value={type}
            options={types}
            onChange={(e: { value: any }) => setType(e.value)}
            placeholder={T("action.select-a-type")}
          />
        </div>
      </div>

      <Editor
        height="500px"
        key="component-editor"
        defaultLanguage="json"
        value={value}
        onChange={onEditorChange}
        onMount={onEditorMounted}
        onValidate={validated => setError(!!validated.length)}
        theme="vs-dark"
      />

      <div className="flex justify-content-end">
        <Button
          icon="pi pi-save"
          label={T("action.save")}
          onClick={() => onSaveButtonClick()}
          disabled={error}
        />
      </div>
    </div>
  );
};

export default ComponentConfiguration;
